import { createSelector } from "reselect";
import { getAllCoachRecommendSteps } from "@microdoing/shared/services/coaching/selectors";
import { IEvents, IReviewType } from "./types";
import { defaultStepFeedback } from "./defaultStates";
import { AppState } from "../rootReducer";
import { getTime } from "@microdoing/shared/services/general/selectors";
import moment from "moment";
import { getSortedConversations } from "../message/selectors";
import { getUserGoalNumber } from "../user/selectors";

import i18n from "@microdoing/shared/i18n";
import { getMbti } from "../profileSettings/selectors";

export const getSelectionRequesting = (state: AppState) =>
  state.userCampaign.selectionRequesting;

export const getCampaignId = (state: AppState) =>
  state.userCampaign.campaignSelected.campaignId;

export const getTeam = (state: AppState) =>
  state.userCampaign.team || { users: [] };

export const getTeamConversation = createSelector(
  getTeam,
  getSortedConversations,
  (team, conversations) => {
    const { conversationId } = team;
    return (
      conversations.find(el => el._id === conversationId) || {
        conversationId: "",
        latestMessage: {
          createdAt: "",
          text: "",
          seen: true,
          seenBy: [],
          user: { _id: "" },
        },
      }
    );
  },
);

export const getTeamName = (state: AppState) =>
  state.userCampaign.team?.name || "";

export const getTeamRequesting = (state: AppState) =>
  state.userCampaign.teamRequesting;

export const getUserCampaignId = (state: AppState) =>
  state.userCampaign.campaignSelected?._id || "";

export const getTrackId = (state: AppState) =>
  state.userCampaign?.campaignSelected?.trackId || "";

export const getTeamId = (state: AppState) => state.userCampaign?.team?._id || "";

export const getCampaignSteps = (state: AppState) =>
  state.userCampaign.campaignSelected?.steps || [];

export const getCampaignName = (state: AppState) =>
  state.userCampaign.campaignSelected?.title;

export const getCampaignWeekStreak = (state: AppState) =>
  state.userCampaign.campaignSelected?.weekStreak;

export const getCampaignPoints = (state: AppState) =>
  state.userCampaign.campaignSelected?.points || 0;

export const getUserCampaignIsDone = (state: AppState) =>
  state.userCampaign.campaignSelected?.isDone || false;

export const getUserCampaignIsRemoved = (state: AppState) =>
  state.userCampaign.campaignSelected?.isRemoved || false;

export const getCampaignSelected = (state: AppState) =>
  state.userCampaign?.campaignSelected || {};

const getRecommendedSteps = (state: AppState) =>
  state.userCampaign.recommendedSteps || [];

export const getCampaignRequesting = (state: AppState) =>
  state.userCampaign.requesting;

export const getUserCampaignStepCommentRequesting = (state: AppState) =>
  state.userCampaign.stepSelected?.stepCommentRequesting;

export const getUserCampaignHasPeerToPeer = (state: AppState) =>
  state.userCampaign.campaignSelected?.hasPeerToPeer || false;

export const getUserCampaignStreak = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.weekStreak;
  },
);

export const getUserCampaignTeamSize = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.teamSize;
  },
);

export const getUserCampaignPoints = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.points;
  },
);

export const getUserCampaignStrategy = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.strategy;
  },
);

export const getNextCategory = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.nextCategory;
  },
);

export const getRecommendedStepsInOrder = createSelector(
  getAllCoachRecommendSteps,
  getRecommendedSteps,
  (coachRecommendedSteps, recommendedSteps) => {
    // put coachingSteps in first position
    return recommendedSteps.sort((a, b) => {
      // true values first
      const isACoaching = coachRecommendedSteps.find(el => el === a._id);
      const isBCoaching = coachRecommendedSteps.find(el => el === b._id);
      return isACoaching === isBCoaching ? 0 : isACoaching ? -1 : 1;
    });
  },
);

export const getCampaignSkills = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.skills;
  },
);

export const getOnboardingSurveyId = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.onboardingSurveyId;
  },
);

export const getHasRequestedForNewActions = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.hasRequestedForNewActions;
  },
);

export const getIsCoachingCampaign = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return !!campaignSelected.isCoachingCampaign || false;
  },
);

export const getIsRankingDisabled = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return !!campaignSelected.isRankingDisabled || false;
  },
);

export const getCampaignStartDate = (state: AppState) =>
  state.userCampaign?.campaignSelected?.startDate;

export const getCampaignEndDate = (state: AppState) =>
  state.userCampaign?.campaignSelected?.endDate;

export const getStepSelected = (state: AppState) =>
  state.userCampaign.stepSelected || {
    content: { how: {}, why: {}, title: {} },
  };

export const getStepSelectedTotalDone = createSelector(
  getStepSelected,
  stepSelected => {
    return stepSelected.totalDone;
  },
);

export const getStepSelectedId = (state: AppState) =>
  state.userCampaign.stepSelected?.step ||
  state.userCampaign.stepSelected?.stepId ||
  "";

export const getIsTeamVisible = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.teamSize > 1;
  },
);

export const getTotalDoneSteps = createSelector(getCampaignSteps, steps => {
  return steps.reduce((prev, curr) => {
    return prev + (curr?.done ? 1 : 0);
  }, 0);
});

export const getDoneSteps = createSelector(getCampaignSteps, steps => {
  return steps.filter(step => step.done) || [];
});

export const getTotalDoneStepsThisWeek = createSelector(
  getDoneSteps,
  doneSteps => {
    return doneSteps.filter(el => moment(el?.doneDate)?.isSame(moment(), "week"))
      ?.length || 0;
  },
);

export const getCampaignProgression = createSelector(
  getTotalDoneSteps,
  getCampaignSteps,
  (totalDoneSteps, steps) => {
    if (!totalDoneSteps || !steps.length) return 0;
    return totalDoneSteps / steps.length;
  },
);

export const getUserCampaignResourcesStats = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.resourcesStats || [];
  },
);

export const getDebriefText = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.debriefText || {};
  },
);

export const getIsFeedbackRequired = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.isFeedbackRequired || false;
  },
);

export const getHasCampaignReviews = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.hasReviews || false;
  },
);

export const getCampaignFrequency = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.frequency || "oneADay";
  },
);

export const getCampaignSelectedSteps = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.steps || [];
  },
);

export const getCampaignActionGoal = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.campaignActionGoal || 6;
  },
);

export const getUserCampaignCertificate = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.certificateUrl || "";
  },
);

export const getActiveStep = createSelector(getCampaignSelectedSteps, steps => {
  return (
    steps.find(el => !el.done) || {
      _id: "",
      step: "",
      acceptDate: "",
      category: "",
      content: { title: { fr: "", en: "" } },
      comments: [],
      numberOfComments: 0,
      lastStepComment: {},
      users: [],
      requestedResponses: false,
      status: "inactive",
    }
  );
});

export const getIsValidateStepDisabled = createSelector(
  getCampaignSteps,
  getTime,
  getHasCampaignReviews,
  getCampaignFrequency,
  (steps, time, hasReviews, frequency) => {


    const now = moment(time);
    const currentStepIndex = steps.findIndex(el => !el.done);
    let latest = moment().subtract(1, "days");
    if (steps?.[currentStepIndex]?.acceptDate) {

      latest = moment(steps[currentStepIndex].acceptDate);
    }
    const isTime = now.diff(latest, "seconds");

    return steps?.[0]?.done && isTime < 120;
  },
);

export const getIsMbtiRequired = createSelector(
  getCampaignSelected,
  campaignSelected => {
    return campaignSelected.isMbtiRequired || false;
  },
);

export const getIsMbtiPopupDisplayed = createSelector(
  getMbti,
  getIsMbtiRequired,
  getTotalDoneSteps,
  (mbti, isMbtiRequired, totalDoneSteps) => {
    if (!isMbtiRequired) {
      return false;
    }
    if (!mbti) {
      return totalDoneSteps >= 1;
    }
    return false;
  },
);

export const getActiveStepId = createSelector(getActiveStep, selectedStep => {
  return selectedStep.step;
});

export const getActiveStepStatus = createSelector(getActiveStep, activeStep => {
  return activeStep?.status;
});

// export const getActiveStepId = createSelector(
//   getActiveStep,
//   selectedStep => {
//     return selectedStep.step
//   },
// );

export const getIsValidateRequesting = (state: AppState) =>
  state.userCampaign.isValidateRequesting || false;

// export const getStepFeedbacks: IReviewType[] = (state: RootState) =>
//   state.userCampaign.stepFeedbacks || [];

export const getStepFeedbacks = (state: {
  userCampaign: { stepFeedbacks: IReviewType[] };
}) =>
  state.userCampaign.stepFeedbacks?.sort(
    (a, b) => +new Date(b.createdAt) - +new Date(a.createdAt),
  ) || [];

export const getReviewsRequesting = (state: AppState) =>
  state.userCampaign.reviewsRequesting || false;

export const getStepFeedbackSelectedId = (state: AppState) =>
  state.userCampaign.stepFeedbackSelectedId;

export const getReviewSelected = createSelector(
  getStepFeedbacks,
  getStepFeedbackSelectedId,
  (stepFeedbacks, stepFeedbackSelectedId) => {
    return (
      stepFeedbacks.find(el => el._id === stepFeedbackSelectedId) ||
      defaultStepFeedback
    );
  },
);

export const getReviewSelectedCorrectorId = createSelector(
  getReviewSelected,
  reviewSelected => {
    return reviewSelected?.review?.corrector?.correctorId || "";
  },
);

export const getUserCampaignRank = (state: AppState) =>
  state.userCampaign.userRank || 1;

export const getUserCampaignTeamRank = (state: AppState) =>
  state.userCampaign.teamRank || 1;

export const getPreviousUserCampaignTeamRank = (state: AppState) =>
  state.userCampaign.previousTeamRank || 1;

export const getPreviousUserCampaignRank = (state: AppState) =>
  state.userCampaign.previousUserRank || 1;

export const getEvents = (state: AppState) => state.userCampaign?.events || [];

export const getIsCampaignLockedForToday = createSelector(
  getTime,
  getDoneSteps,
  getCampaignFrequency,
  (time, doneSteps, frequency) => {
    const lastDoneStepIndex = doneSteps.length - 1;
    if (lastDoneStepIndex < 0) return false;
    if (frequency === "oneADay") {
      // at least one step done before blocking
      if (lastDoneStepIndex < 0) return false;
      const latest = moment(doneSteps[lastDoneStepIndex].acceptDate);

      return moment(time).isSame(latest, "day");
    } else if (frequency === "twoADay") {
      // Not enough step done
      if (lastDoneStepIndex - 1 < 0) return false;
      const latestDate = moment(doneSteps[lastDoneStepIndex].acceptDate);
      const beforeLatestDate = moment(
        doneSteps[lastDoneStepIndex - 1].acceptDate,
      );
      return (
        latestDate.isSame(beforeLatestDate, "day") &&
        moment(time).isSame(latestDate, "day")
      );
    }
    return false;
  },
);

export const getEventsWithActionGoals = createSelector(
  getEvents,
  getCampaignActionGoal,
  getTotalDoneSteps,
  getActiveStepStatus,
  (events, campaignGoals, totalDoneSteps, activeStatus) => {
    const now = moment();
    const today = moment();

    const malus = activeStatus === "selected" ? -1 : 0;

    let pendingDays = campaignGoals - totalDoneSteps + malus;
    if (pendingDays <= 0) {
      pendingDays = 4;
    }

    const fakeTimeline = [...new Array(pendingDays)].map((el, i) => {
      now.add(1, "day");

      if (now.weekday() === 5) {
        now.add(2, "day");
      }

      return {
        type: "defi",
        title: i18n.t("new-challenge"),
        date: moment(now).toDate(),
      };
    });

    const result = [...events, ...fakeTimeline]
      .filter(el => moment(el.date).isAfter(today))
      .sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });
    return result;
  },
);

export const getTimelineEvents = createSelector(
  getEvents,
  getUserGoalNumber,
  (events, userGoalNumber) => {
    const sortedEvents = events
      ?.sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      })
      .filter(event => {
        const eventDate = moment(new Date(event.date)).format("DD/MM/YYYY");
        const todayDate = moment(new Date()).format("DD/MM/YYYY");
        return eventDate >= todayDate;
      });

    const groupEventsByWeek = events => {
      return Object.values(
        events.reduce((acc, event) => {
          const date = moment(event.date);
          const week = date.week();
          if (!acc[week]) {
            acc[week] = [];
          }
          acc[week].push(event);
          return acc;
        }, {}),
      );
    };
  },
);
